import { ArrowPathIcon, ComputerDesktopIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { AsyncStatus } from '../../../@types/AsyncStatus';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Popup from '../../common/Popup';
import { SetupPage, updateSetupState } from '../../common/SetupPage';
import Spinner from '../../common/Spinner';
import AddressPanel from './components/AddressPanel';
import { useAddressQualifier } from './components/AddressQualifier';
import CoproList from './components/CoproList';
import QualificationMap from './components/QualificationMap';
import type { RnicEntry, SelectedCopro } from './types';

const fetchCoprosData = async(
  companyId: number,
  jwt: string,
  setRnics: (rnics: RnicEntry[]) => void,
  setAllCopros: (copros: SelectedCopro[]) => void,
  setStatus: (status: AsyncStatus) => void,
) => {
  setStatus('PENDING');
  try {
    const response = await fetch(
      `/api/companies/${companyId}/copros?limit=10000&filterValidatedByUser=true&sortField=id&sortDirection=desc`,
      JWTHeader(jwt),
    );
    const result = await response.json();

    const responseRNIC = await fetch(`/api/companies/${companyId}/rnics`, JWTHeader(jwt));
    const coprosRNIC = await responseRNIC.json();
    setRnics(coprosRNIC);

    // Keep only copros that belong to this company. We intentionally include AFUL, ASL,
    // immeubles tertiaires and "Autre" (which don't have a RNIC remoteId) so the user
    // can validate their addresses & parcelles too.
    const filteredResults = result.results.filter(
      (copro: SelectedCopro) => copro.company.id === companyId,
    );

    // Sort: copros with the least plots first, then the least addresses. This puts
    // truly empty copros (0 plots / 0 addresses) at the top as "à traiter en priorité",
    // followed by plots-only copros (BAN edge case), and finally the fully localized ones.
    const sortedResults = filteredResults.sort((a: SelectedCopro, b: SelectedCopro) => {
      const plotsDiff = a.plots.length - b.plots.length;
      if (plotsDiff !== 0) { return plotsDiff; }
      return a.addresses.length - b.addresses.length;
    });

    setAllCopros(sortedResults);
    setStatus('SUCCESS');
  } catch (error) {
    console.error('Error fetching data:', error);
    setStatus('ERROR');
  }
};

function ActivitySetupStepAddressVerification() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [allCopros, setAllCopros] = useState<SelectedCopro[] | null>(null);
  const [rnics, setRnics] = useState<RnicEntry[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');

  const [selectedCopro, setSelectedCopro] = useState<SelectedCopro | null>(null);

  const [isTop, setIsTop] = useState(true);
  const [isBottom, setIsBottom] = useState(false);
  const [showSmallScreenWarning, setShowSmallScreenWarning] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  // Tailwind md breakpoint
  const MD_BREAKPOINT = 768;

  const fetchData = useCallback(() => {
    fetchCoprosData(company.id, jwt, setRnics, setAllCopros, setStatus);
  }, [company.id, jwt]);

  // Callback for when copro is successfully updated
  const handleCoproUpdate = useCallback((updatedCopro?: SelectedCopro) => {
    if (!updatedCopro) {
      fetchData();
      setSelectedCopro(null);
      return;
    }

    // Re-attach details/RNIC info if missing
    if (!updatedCopro.details || !updatedCopro.details.adresseDeReference) {
      const rnicEntry = rnics.find((r) => r.numeroDimmatriculation === updatedCopro.remoteId);
      if (rnicEntry) {
        updatedCopro.details = {
          ...updatedCopro.details,
          adresseDeReference: rnicEntry.adresseDeReference,
        };
      }
    }

    setAllCopros((prev) => {
      if (!prev) { return prev; }
      return prev.map((c) => (c.id === updatedCopro.id ? updatedCopro : c));
    });

    // Reset selected copro to show "Sélectionnez une copro" state
    setSelectedCopro(null);
  }, [rnics, fetchData]);

  // Initialize the address qualifier hook
  const qualifier = useAddressQualifier(selectedCopro, handleCoproUpdate);

  // Check for small screen and show warning
  useEffect(() => {
    const checkScreenSize = () => {
      if (window.innerWidth < MD_BREAKPOINT) {
        setShowSmallScreenWarning(true);
      }
    };

    // Check on mount
    checkScreenSize();

    // Also check on resize in case user resizes to a smaller screen
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Intersection observers for sticky button
  useEffect(() => {
    const topObserver = new IntersectionObserver(
      ([entry]) => setIsTop(entry.isIntersecting),
      { threshold: 1 },
    );

    const bottomObserver = new IntersectionObserver(
      ([entry]) => setIsBottom(entry.isIntersecting),
      { threshold: 0 },
    );

    if (topRef.current) {
      topObserver.observe(topRef.current);
    }
    if (bottomRef.current) {
      bottomObserver.observe(bottomRef.current);
    }

    return () => {
      if (topRef.current) {
        topObserver.unobserve(topRef.current);
      }
      if (bottomRef.current) {
        bottomObserver.unobserve(bottomRef.current);
      }
    };
  }, []);

  const handleSelectCopro = useCallback((copro: SelectedCopro) => {
    // Attach RNIC details if missing
    if (!copro.details || !copro.details.adresseDeReference) {
      const rnicEntry = rnics.find((r) => r.numeroDimmatriculation === copro.remoteId);
      if (rnicEntry) {
        copro.details = {
          ...copro.details,
          adresseDeReference: rnicEntry.adresseDeReference,
        };
      }
    }
    setSelectedCopro(copro);
    qualifier.initializeCopro(copro);

    // Scroll the right panel into view on mobile/tablet or when not fully visible
    setTimeout(() => {
      if (rightPanelRef.current) {
        rightPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      }
    }, 100);
  }, [rnics, qualifier]);

  const handleNextStep = async() => {
    if (status === 'PENDING' || status === 'IDLE') {
      return;
    }
    await updateSetupState(company, jwt, 'addressValidation', dispatch);
    navigate(ROUTES.SETUP_TRANSACTION_QUALIFICATION);
  };

  useEffect(() => {
    if (jwt && company && company.id && status !== 'PENDING') {
      fetchData();
    }
  }, [jwt, company]);

  const renderSaveButton = () => (
    <div
      className={classNames(
        'text-right w-full md:w-auto mt-10',
        !isTop && !isBottom && 'fixed z-[1000] right-0 bottom-0 md:right-5 md:bottom-5 bg-white rounded',
      )}
    >
      <button
        onClick={handleNextStep}
        className={classNames(
          'bg-rgpd-green text-white text-xl px-8 py-4 shadow-md align-center w-full md:w-auto rounded',
          (status === 'PENDING' || status === 'IDLE') && 'opacity-80 cursor-not-allowed',
        )}
      >
        Prochaine étape
      </button>
    </div>
  );

  return (
    <SetupPage
      step={'addressValidation'}
      title="VÉRIFICATION DES ADRESSES"
      children={(
        <>
          <div ref={topRef} style={{ height: '1px' }} />
          
          {/* Header section */}
          <div className="mb-8 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="flex flex-col items-start justify-between gap-6">
              <div className="w-full px-1">
                <h3 className="mb-4 text-lg font-semibold text-gray-800">
                  A CETTE ÉTAPE, VOUS DEVEZ VALIDER LES ADRESSES ET PARCELLES DE
                  {' '}
                  <span className="text-rgpd-blue-light">CHACUNE DE VOS COPROPRIÉTÉS.</span>
                </h3>
                <p className="leading-relaxed text-gray-600">
                  Dans le tableau ci-dessous, vous trouverez l'ensemble de vos copropriétés.
                  {' '}
                  <br />
                  <span className="font-medium text-rgpd-blue-light">
                    Les copropriétés sans adresse valide sont affichées en premier et sont à traiter en priorité.
                  </span>
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                  <span className="font-medium text-gray-700">Légende :</span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm bg-rgpd-blue-dark" aria-hidden="true" />
                    À traiter (aucune localisation)
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm bg-orange-500" aria-hidden="true" />
                    Validable via parcelles uniquement (aucune adresse trouvée dans la Base Adresse Nationale)
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm bg-rgpd-green" aria-hidden="true" />
                    Localisée (au moins une adresse et une parcelle)
                  </span>
                </div>
                <div className="mt-6 rounded-lg border border-blue-100 bg-blue-50 p-4">
                  <h3 className="flex items-center font-semibold text-rgpd-blue-dark">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mr-2 h-6 w-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                    </svg>
                    Cette étape est importante pour la bonne reconnaissance des ventes et des DPEs.
                  </h3>
                </div>
              </div>
            </div>
            
            <div className="mt-8 border-t border-gray-100 pt-6">
              <h2 className="flex items-center gap-3 text-xl font-bold text-gray-800">
                <span className="rounded bg-rgpd-blue-light px-3 py-1 text-sm tracking-wide whitespace-nowrap text-white uppercase">
                  ÉTAPE N°3 :
                </span>
                <span>VÉRIFICATION DES ADRESSES</span>
              </h2>
            </div>
          </div>

          {status === 'PENDING' && (
            <p className="mx-10 my-32 text-center">
              <Spinner aria-label="Center-aligned spinner" size="xl" />
            </p>
          )}

          {status === 'SUCCESS' && allCopros && (
            <div className="relative flex gap-2 xl:gap-6">
              {/* Left panel - Copro list */}
              <div className="w-1/2 pr-2">
                <CoproList
                  copros={allCopros}
                  rnics={rnics}
                  selectedCoproId={selectedCopro?.id || null}
                  onSelectCopro={handleSelectCopro}
                  status={status}
                />
              </div>

              {/* Right panel - Map and Addresses (sticky) */}
              <div 
                ref={rightPanelRef}
                className="sticky top-4 flex w-1/2 flex-col self-start"
                style={{ height: 'calc(100vh - 250px)' }}
              >
                {/* Loading overlay */}
                {qualifier.isLoading && (
                  <div className="absolute inset-0 z-[1000] flex items-center justify-center rounded-lg bg-black/50">
                    <ArrowPathIcon className="h-12 w-12 animate-spin text-white" />
                  </div>
                )}

                {/* Map + Address panel side by side */}
                <div className="flex h-full min-h-0 gap-2">
                  {/* Map section */}
                  <div className="h-full min-h-0 w-1/2">
                    <QualificationMap
                      selectedCopro={selectedCopro}
                      parcelles={qualifier.parcelles}
                      mapCenter={qualifier.mapCenter}
                      mapZoom={qualifier.mapZoom}
                      addressFound={qualifier.addressFound}
                      onMapClick={qualifier.handleMapClick}
                      onParcelleClick={qualifier.handleParcelleClick}
                      onSearchSelect={qualifier.handleSearchSelect}
                      onUseInSearch={qualifier.handleUseInSearch}
                      searchQuery={qualifier.searchQuery}
                      setSearchQuery={qualifier.setSearchQuery}
                      shouldUpdateView={qualifier.shouldUpdateView}
                      setShouldUpdateView={qualifier.setShouldUpdateView}
                      mapRef={qualifier.mapRef}
                    />
                  </div>

                  {/* Address panel */}
                  <div className="h-full min-h-0 w-1/2">
                    <AddressPanel
                      selectedCopro={selectedCopro}
                      parcelles={qualifier.parcelles}
                      addresses={qualifier.addresses}
                      manualAddresses={qualifier.manualAddresses}
                      onAddressCheck={qualifier.handleAddressCheck}
                      onSelectAll={qualifier.handleSelectAll}
                      onDeselectAll={qualifier.handleDeselectAll}
                      onAddManualAddress={qualifier.handleAddManualAddress}
                      onDeleteManualAddress={qualifier.handleDeleteManualAddress}
                      onAddressClick={qualifier.handleAddressClick}
                      onSave={() => qualifier.handleConfirmSave()}
                      isSaving={qualifier.isSaving}
                      showManualAddressInput={qualifier.showManualAddressInput}
                      setShowManualAddressInput={qualifier.setShowManualAddressInput}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {status === 'ERROR' && (
            <div className="my-32 flex w-full place-content-center">
              <div className="rounded-lg bg-red-100 px-6 py-4 text-red-700">
                Erreur lors du chargement des données
              </div>
            </div>
          )}

          <div ref={bottomRef} style={{ height: '1px' }} />

          {renderSaveButton()}

          {/* Confirmation popup upon "Enregistrer" */}
          <Popup
            isOpen={qualifier.isConfirmationOpen}
            onClose={() => qualifier.setIsConfirmationOpen(false)}
            title="Confirmation des adresses"
            width="500px"
            height="auto"
          >
            <div>
              {(() => {
                const allSelectedAddresses = [
                  ...qualifier.addresses.filter((addr) => addr.selected),
                  ...qualifier.manualAddresses.filter((addr) => addr.selected),
                ];
                const uniqueSelectedAddresses = allSelectedAddresses.filter(
                  (addr, index, self) => self.findIndex((a) => a.id === addr.id) === index,
                );

                return uniqueSelectedAddresses.length > 0 ? (
                  <>
                    <p className="mb-2 text-sm text-gray-500">
                      {uniqueSelectedAddresses.length === 1 ? "Vous validez l'adresse :" : 'Vous validez les adresses :'}
                    </p>
                    <ul className="list-inside list-disc">
                      {uniqueSelectedAddresses.map((addr, index) => (
                        <li key={index} className="text-sm">
                          {addr.address}, {addr.postalCode} {addr.city}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="my-2 text-sm text-gray-500">
                    Aucune adresse n'a été sélectionnée.
                    <br />
                    Vous pouvez en renseigner manuellement dans la partie "Vous ne trouvez pas une adresse ?"
                  </p>
                );
              })()}
              {qualifier.addresses.some((addr) => !addr.selected) && (
                <>
                  <p className="mt-4 mb-2 text-sm text-gray-500">
                    {qualifier.addresses.filter((addr) => !addr.selected).length === 1
                      ? "Vous ne validez pas l'adresse :"
                      : 'Vous ne validez pas les adresses :'}
                  </p>
                  <ul className="list-inside list-disc">
                    {qualifier.addresses.filter((addr) => !addr.selected).map((addr, index) => (
                      <li key={index} className="text-sm">
                        {addr.address}, {addr.postalCode} {addr.city}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <p className="text-md mt-4 mb-2 font-semibold">Êtes-vous sûr ?</p>
            </div>

            <div className="mt-4 flex justify-between">
              <button
                type="button"
                className="inline-flex justify-center rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-rgpd-white hover:bg-red-600/80"
                onClick={() => qualifier.setIsConfirmationOpen(false)}
              >
                NON
              </button>
              <button
                type="button"
                className="inline-flex justify-center rounded-xl bg-rgpd-green px-4 py-2 text-sm font-medium text-white hover:bg-rgpd-green-dark"
                onClick={qualifier.handleSave}
              >
                OUI
              </button>
            </div>
          </Popup>

          {/* Popup for last autocomplete result */}
          <Popup
            isOpen={qualifier.showLastResultPopup}
            onClose={() => qualifier.setShowLastResultPopup(false)}
            title={(
              <div className="flex items-center">
                <ExclamationTriangleIcon className="mr-2 h-6 w-6 text-yellow-500" />
                <p className="text-lg font-semibold">Attention</p>
              </div>
            )}
            width="500px"
            height="auto"
          >
            <div>
              <p className="text-md mb-2 text-gray-500">
                La dernière adresse recherchée n'a pas été sélectionnée :
              </p>
              <p className="font-semibold">
                {qualifier.lastAutocompleteResult && 
                  `${qualifier.lastAutocompleteResult.properties.name}, ${qualifier.lastAutocompleteResult.properties.postcode} ${qualifier.lastAutocompleteResult.properties.city}`}
              </p>
              <p className="text-md mt-4 text-gray-500">
                Voulez-vous l'ajouter aux adresses sélectionnées ?
              </p>
            </div>

            <div className="mt-4 flex justify-between">
              <button
                type="button"
                className="inline-flex justify-center rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-rgpd-white hover:bg-red-600/80"
                onClick={() => {
                  qualifier.setShowLastResultPopup(false);
                  qualifier.handleConfirmSave(true);
                }}
              >
                NON
              </button>
              <button
                type="button"
                className="inline-flex justify-center rounded-xl bg-rgpd-green px-4 py-2 text-sm font-medium text-white hover:bg-rgpd-green-dark"
                onClick={qualifier.handleAddLastResult}
              >
                OUI
              </button>
            </div>
          </Popup>

          {/* Small screen warning popup */}
          <Popup
            isOpen={showSmallScreenWarning}
            onClose={() => setShowSmallScreenWarning(false)}
            title={(
              <div className="flex items-center">
                <ComputerDesktopIcon className="mr-2 h-6 w-6 text-rgpd-blue-light" />
                <p className="text-lg font-semibold">Écran trop petit</p>
              </div>
            )}
            width="400px"
            height="auto"
          >
            <div className="text-center">
              <div className="mb-4">
                <ComputerDesktopIcon className="mx-auto h-16 w-16 text-rgpd-blue-light" />
              </div>
              <p className="mb-4 text-gray-600">
                Cette étape de vérification des adresses nécessite un affichage plus large pour une expérience optimale.
              </p>
              <p className="mb-6 text-gray-600">
                Pour un meilleur confort d'utilisation, nous vous recommandons d'accéder à cette page depuis un <span className="font-semibold">ordinateur</span> ou une <span className="font-semibold">tablette en mode paysage</span>.
              </p>
              <button
                type="button"
                className="inline-flex w-full justify-center rounded-xl bg-rgpd-blue-light px-6 py-3 text-sm font-medium text-white hover:brightness-110"
                onClick={() => setShowSmallScreenWarning(false)}
              >
                J'ai compris
              </button>
            </div>
          </Popup>
        </>
      )}
    />
  );
}

export default ActivitySetupStepAddressVerification;
